import { useEffect, useState } from "react";
import { IRole } from "./../../../commons/interfaces";

import Helpers from "../../../commons/helpers";
import { RoleHelpers } from "../../../commons/roleHelpers";
import { Constants } from "../../../constants/constants";
import { RoleTab, Status } from "../../../constants/enums";
import Strings from "../../../constants/strings";
import { useCommonComponentContext } from "../../../providers/CommonComponentProvider";
import RoleService, { IRequestGetPagedRole } from "../../../services/identity/role.serice";

export interface IListRoleModel {
    requestData?: IRequestGetPagedRole;
    requestDataTemp?: IRequestGetPagedRole;
}

interface IProps {
    request: IRequestGetPagedRole;
    onGetPaged: (data: { query: string; totalCount: number }) => void;
}

export const useData = ({ request, onGetPaged }: IProps) => {
    const {
        clientId,
        userInfo,
        tenantCode,
        serviceCode,
        onError = () => {},
        onSuccess = () => {},
        onShowLoading = () => {},
        onHideLoading = () => {},
    } = useCommonComponentContext();

    const [rows, setRows] = useState<IRole[]>([]);
    const [loadingDataTable, setLoadingDataTable] = useState<boolean>(false);
    const [model, setModel] = useState<IListRoleModel>({} as IListRoleModel);

    useEffect(() => {
        const onStarted = async () => {
            const newReq: IRequestGetPagedRole = {
                totalCount: request?.totalCount || 0,
                pageSize: request?.pageSize || model?.requestData?.pageSize,
                pageNumber: request?.pageNumber || model?.requestData?.pageNumber,
                searchText: request?.searchText || model?.requestData?.searchText,
                serviceCode: request?.serviceCode || model?.requestData?.serviceCode,
                tab: RoleTab.Role,
            };

            await getPaged(newReq);
        };
        onStarted();
    }, [request, userInfo?.userProfile?.organizationId]);

    const getPaged = async (newReq?: IRequestGetPagedRole) => {
        try {
            // onShowLoading();
            setLoadingDataTable(true);

            const pageSize = newReq?.pageSize || Constants.ROW_PER_PAGE;
            const pageNumber = Helpers.getPageNumber(newReq?.pageNumber || 1, pageSize, newReq?.totalCount || 0) || 1;

            const requestData: IRequestGetPagedRole = {
                pageSize,
                pageNumber,
                clientId,
                searchText: newReq?.searchText,
                serviceCode: newReq?.serviceCode,
                organizationId: userInfo?.userProfile?.organizationId,
            };

            const resultGetPaged = await RoleService.getPaged(requestData);

            let query: string = `?pageNumber=${requestData.pageNumber}&pageSize=${requestData.pageSize}`;
            if (!Helpers.isNullOrEmpty(requestData?.serviceCode)) {
                query += `&serviceCode=${requestData?.serviceCode}`;
            }
            if (!Helpers.isNullOrEmpty(requestData?.searchText)) {
                query += `&searchText=${requestData?.searchText}`;
            }
            if (!Helpers.isNullOrEmpty(newReq?.tab)) {
                query += `&tab=${newReq?.tab}`;
            }

            setRows(resultGetPaged.items);
            setModel({
                requestData: {
                    ...requestData,
                    totalCount: resultGetPaged.totalCount,
                    tab: newReq.tab,
                },
                requestDataTemp: {
                    ...requestData,
                    totalCount: resultGetPaged.totalCount,
                    tab: newReq.tab,
                },
            });

            onGetPaged({ query, totalCount: resultGetPaged.totalCount });
        } catch (error) {
            const e = Helpers.renderExceptionError(error);
            onError(e);
        } finally {
            // onHideLoading();
            setLoadingDataTable(false);
        }
    };

    const handleDelete = (row: any) => {
        Helpers.showConfirmAlert(Strings.Role.CONFIRM_DELETE, async () => {
            try {
                onShowLoading();

                await RoleService.delete({ id: row?.id });

                await getPaged(model.requestData);

                onSuccess(Strings.Role.DELETE_SUCCESS);
            } catch (error) {
                const e = Helpers.renderExceptionError(error);
                onError(e);
            } finally {
                onHideLoading();
            }
        });
    };

    const handleChangeStatus = (data: any) => {
        Helpers.showConfirmAlert(Strings.Role.CONFIRM_CHANGE_STATUS, async () => {
            try {
                onShowLoading();

                let numberOfUser = 0;

                if (data?.status === Status.Active) {
                    numberOfUser = await RoleService.countUser(data?.id);
                }

                if (numberOfUser > 0 && RoleHelpers.isOrganizationUser(userInfo?.userProfile)) {
                    Helpers.showAlert(`Đang có ${numberOfUser} người đang sử dụng! Không thể thực hiện thao tác.`, "error");
                    return;
                }

                if (numberOfUser > 0 && !RoleHelpers.isOrganizationUser(userInfo?.userProfile)) {
                    Helpers.showConfirmAlert(`Đang có ${numberOfUser} người đang sử dụng! Bạn có muốn thực hiện thao tác`, async () => {
                        await RoleService.updateStatus({
                            id: data?.id,
                            isActive: data?.status === Status.Active ? false : true,
                        });

                        await getPaged(model.requestData);

                        onSuccess(Strings.Role.UPDATE_STATUS_SUCCESS);
                    });
                    return;
                }

                await RoleService.updateStatus({
                    id: data?.id,
                    isActive: data?.status === Status.Active ? false : true,
                });
                await getPaged(model.requestData);

                onSuccess(Strings.Role.UPDATE_STATUS_SUCCESS);
            } catch (error) {
                const e = Helpers.renderExceptionError(error);
                onError(e);
            } finally {
                onHideLoading();
            }
        });
    };

    const onChangeValue = (key: keyof IRequestGetPagedRole) => (value: string | number) => {
        setModel((prev) => ({ ...prev, requestDataTemp: { ...prev.requestDataTemp, [key]: value } }));
    };

    const onResetModel = () => setModel({ ...model, requestDataTemp: model.requestData });

    return {
        rows,
        model,
        getPaged,
        onResetModel,
        handleDelete,
        onChangeValue,
        handleChangeStatus,
        loadingDataTable,
    };
};

export default useData;
